import React from "react";
import { Helmet } from "react-helmet-async";
import {
  SITE_NAME,
  DEFAULT_TITLE,
  DEFAULT_DESCRIPTION,
  DEFAULT_IMAGE,
} from "./seoConfig";
import { toAbsoluteUrl } from "./seoUtils";

// Returns undefined for empty/null/undefined so callers never render
// content="undefined" or content="null".
const clean = (value) => {
  if (value === null || value === undefined) return undefined;
  const str = String(value).trim();
  return str.length ? str : undefined;
};

/**
 * Reusable per-route SEO tag renderer built on react-helmet-async.
 *
 * Canonical URL / og:url are intentionally NOT rendered here by default.
 * They are handled once, globally, by the <CanonicalTag> in src/App.js so
 * that every route (including ones that never render <Seo>) reliably gets
 * exactly one canonical tag with zero risk of a page forgetting it or
 * duplicating it. The `canonical` prop is still accepted for flexibility,
 * and `centralizedSeo.canonical_url` (see below) can override it - in both
 * cases react-helmet-async's own dedupe (deepest-rendered Helmet instance
 * wins for singleton tags like `<link rel="canonical">`) guarantees only
 * one canonical tag ever survives, since <Seo> always renders deeper in the
 * tree than <CanonicalTag>.
 *
 * `centralizedSeo` is the optional, already-fetched record from the Laravel
 * `GET {API_BASE}seo?path=` endpoint (see useSeoMeta.js). Pass it only from
 * static/indexable route components. It takes priority over the `title`/
 * `description`/`canonical`/`noindex` props, which in turn take priority
 * over the site-wide defaults - see each prop's resolution below. Package
 * and destination detail pages must NOT fetch or pass this; they already
 * have package.meta_title/description or destination.meta_title/description
 * from their own APIs, which callers should put in the `title`/`description`
 * props directly.
 */
const Seo = ({
  title,
  description,
  canonical,
  image,
  type = "website",
  jsonLd,
  noindex = false,
  centralizedSeo = null,
}) => {
  const centralTitle = clean(centralizedSeo?.meta_title);
  const centralDescription = clean(centralizedSeo?.meta_description);
  const centralCanonical = clean(centralizedSeo?.canonical_url);
  // index_status is resolved independently of meta_title/meta_description -
  // a record with a title but a null description (or vice versa) still
  // applies whichever fields it has (see toMetaDescription-less fallback
  // above). When a centralized record exists and states an index_status,
  // it fully decides indexability in EITHER direction (can turn a locally
  // hardcoded noindex page indexable, not just add noindex on top of it).
  // Only when no record exists (or it omits index_status) does the local
  // `noindex` prop's default apply.
  const centralIndexStatus = centralizedSeo?.index_status;

  const resolvedTitle = centralTitle || clean(title) || DEFAULT_TITLE;
  const resolvedDescription =
    centralDescription || clean(description) || DEFAULT_DESCRIPTION;
  const resolvedCanonical = toAbsoluteUrl(centralCanonical || clean(canonical));
  const resolvedImage = toAbsoluteUrl(clean(image)) || DEFAULT_IMAGE;
  const resolvedNoindex =
    centralIndexStatus === "noindex" || centralIndexStatus === "index"
      ? centralIndexStatus === "noindex"
      : noindex;

  const schemas = Array.isArray(jsonLd)
    ? jsonLd.filter(Boolean)
    : jsonLd
    ? [jsonLd]
    : [];

  return (
    <Helmet>
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      {resolvedCanonical && <link rel="canonical" href={resolvedCanonical} />}

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      {resolvedImage && <meta property="og:image" content={resolvedImage} />}
      {resolvedCanonical && (
        <meta property="og:url" content={resolvedCanonical} />
      )}
      <meta property="og:type" content={type} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      {resolvedImage && <meta name="twitter:image" content={resolvedImage} />}

      {resolvedNoindex && <meta name="robots" content="noindex, nofollow" />}

      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;

export { DEFAULT_TITLE, DEFAULT_DESCRIPTION, DEFAULT_IMAGE };
